import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, of } from 'rxjs';
import { ShippingService, CartShippingItem } from './shipping.service';
import { ShippingConfigService } from '../../core/services/shipping-config.service';
import { catchError } from 'rxjs/operators';

export interface FedexShippingOption {
  serviceType: string;
  serviceName: string;
  totalPrice: number;
  currency: string;
  deliveryDays?: string | null;
  deliveryDate?: string | null;
}

export interface ShippingState {
  loading: boolean;
  provider: 'fedex' | null;
  options: FedexShippingOption[];
  selectedOption: FedexShippingOption | null;
  /** The single price to display everywhere in the app */
  displayPrice: number;
  currency: string;
  error: string | null;
}

const INITIAL_STATE: ShippingState = {
  loading: false,
  provider: null,
  options: [],
  selectedOption: null,
  displayPrice: 0,
  currency: 'EUR',
  error: null,
};

@Injectable({ providedIn: 'root' })
export class ShippingStateService {
  private readonly _state$ = new BehaviorSubject<ShippingState>(INITIAL_STATE);
  private rateSubscription?: Subscription;

  /** Observable for all components to subscribe to */
  readonly state$: Observable<ShippingState> = this._state$.asObservable();

  get currentPrice(): number {
    return this._state$.value.displayPrice;
  }

  get currentState(): ShippingState {
    return this._state$.value;
  }

  constructor(
    private readonly shippingService: ShippingService,
    private readonly shippingConfigService: ShippingConfigService
  ) {}

  /**
   * Fetch real shipping rates from FedEx via backend.
   * Only called when countryCode, postalCode, and city are provided.
   */
  fetchRates(
    cart: CartShippingItem[],
    countryNameOrCode?: string | null,
    postalCode?: string,
    city?: string,
    street?: string,
    state?: string,
  ): void {
    if (this.rateSubscription) {
      this.rateSubscription.unsubscribe();
      this.rateSubscription = undefined;
    }

    if (!cart || cart.length === 0) {
      this._state$.next({ ...INITIAL_STATE });
      return;
    }

    const rawCountry = (countryNameOrCode || '').trim();
    const countryCode = (this.shippingConfigService.resolveCountryCode(rawCountry) || rawCountry).toUpperCase();
    const cleanPostal = (postalCode || '').trim();
    const cleanCity = (city || '').trim();

    // Do NOT call the API while any field is empty or country code is invalid
    if (!countryCode || countryCode.length !== 2 || !cleanPostal || !cleanCity || !street?.trim()) {
      this._state$.next({ ...INITIAL_STATE });
      return;
    }

    this._state$.next({
      ...INITIAL_STATE,
      loading: true,
      currency: this._state$.value.currency,
    });

    const subtotal = cart.reduce((sum, item) => {
      const price = Number((item as any)?.price || (item as any)?.priceHT || (item.product as any)?.priceHT || 0);
      const qty = Number(item.quantity) || 1;
      return sum + price * qty;
    }, 0);

    const recipient = {
      countryCode,
      postalCode: cleanPostal,
      city: cleanCity,
      streetLine: street?.trim(),
      state: state?.trim() || undefined,
    };

    this.rateSubscription = this.shippingService
      .getLiveRates(cart, recipient, subtotal)
      .pipe(
        catchError((error) => {
          return of({
            success: false,
            rates: [],
            error:
              "We couldn't reach the shipping provider, please contact support to complete this order.",
          });
        })
      )
      .subscribe((result: any) => {
        const isSuccess = result?.success === true && result?.provider === 'fedex';
        const rates = result?.rates || [];
        const hasValidRates = Array.isArray(rates) && rates.length > 0;

        // Block user if rate calculation failed or address is invalid
        if (!isSuccess || !hasValidRates) {
          this._state$.next({
            loading: false,
            provider: null,
            options: [],
            selectedOption: null,
            displayPrice: 0,
            currency: result?.currency || 'EUR',
            error:
              result?.error ||
              "We couldn't reach the shipping provider, please contact support to complete this order.",
          });
          return;
        }

        const targetCurrency = result.currency || 'EUR';
        const options: FedexShippingOption[] = rates.map((r: any) => ({
          serviceType: r.serviceType,
          serviceName: r.serviceName,
          totalPrice: Number(r.totalPrice) || 0,
          currency: r.currency || targetCurrency,
          deliveryDays: r.deliveryDays || null,
          deliveryDate: r.deliveryDate || null,
        }));

        // Keep current selected option if still valid, otherwise default to first/cheapest
        const prevSelected = this._state$.value.selectedOption;
        const matched = prevSelected
          ? options.find((o) => o.serviceType === prevSelected.serviceType)
          : null;
        const selected = matched || options[0];

        this._state$.next({
          loading: false,
          provider: result.provider || 'fedex',
          options,
          selectedOption: selected,
          displayPrice: selected?.totalPrice ?? 0,
          currency: targetCurrency,
          error: null,
        });
      });
  }

  /** User picks a different shipping tier */
  selectOption(option: FedexShippingOption): void {
    this._state$.next({
      ...this._state$.value,
      selectedOption: option,
      displayPrice: option.totalPrice,
      currency: option.currency || this._state$.value.currency,
    });
  }

  reset(): void {
    if (this.rateSubscription) {
      this.rateSubscription.unsubscribe();
      this.rateSubscription = undefined;
    }
    this._state$.next({ ...INITIAL_STATE });
  }
}
